<script setup lang="ts">
import { ref, computed, nextTick } from 'vue';
import { Notify, QForm, QInput, useDialogPluginComponent } from 'quasar';
import isEmpty from 'validator/lib/isEmpty';
import isLength from 'validator/lib/isLength';
import FileUploader from '@/components/TicketsAccountingPeriod/FileUploader.vue';
import { CreateTaskInputDto, FileDto } from '@/generated/graphql.ts';
import { useTicketsComposable } from '@/components/TicketsAccountingPeriod/useTicketsComposable.ts';
import { useTicketsPermissions } from '@/components/TicketsAccountingPeriod/useTicketsPermissions.ts';
import { useMessageValidation } from './useMessageValidation';
import TicketContextFiles from '@/components/TicketsAccountingPeriod/TicketContextFiles.vue';
import TicketContextTransaction from '@/components/TicketsAccountingPeriod/TicketContextTransaction.vue';
import { ITicketFileContext, ITicketTransactionContext, MessageGateAction } from '@/components/TicketsAccountingPeriod/ticketsTypes.ts';
const props = defineProps<{
  companyId: string;
  folderId: string;
  periodFolderId: string;
  periodName?: string;
  filesContext?: ITicketFileContext[];
  transactionContext?: ITicketTransactionContext;
}>();

interface FileUploaderComponentType {
  clearFiles(): void;
  uploadPendingFiles(): Promise<string[]>;
}

const { dialogRef: createSubjectTicketDialogRef, onDialogOK } = useDialogPluginComponent();
const { createTicket, sendTicketMessage } = useTicketsComposable();
const { canManageTickets } = useTicketsPermissions();
const subjectTicket = ref<string>('');
const message = ref<string>('');
const localFilesContext = ref<ITicketFileContext[]>(props.filesContext ? [...props.filesContext] : []);
const localTransactionContext = ref<ITicketTransactionContext | null>(props.transactionContext || null);
const formCreateTicketSubjectRef = ref<InstanceType<typeof QForm> | null>(null);
const MAX_LENGTH_SUBJECT = 80;

import {
  PLACEHOLDER_VISIBLE,
  prettifyPlaceholders,
  extractPlaceholderNames,
  getPlaceholderRangeAtPos,
  replacePeriodBeforePrettify,
  PERIOD_PLACEHOLDER_NAME,
} from '@/components/TicketsAccountingPeriod/helpers/placeholderHelpers';

const subjectTicketRules = [
  (val: string) => !isEmpty(val) || 'Subject is required',
  (val: string) => isLength(val, { max: MAX_LENGTH_SUBJECT }) || `Subject cannot exceed ${MAX_LENGTH_SUBJECT} characters`,
];
const messageTicketRules = [
  (val: string) => !isEmpty(val) || 'Message is required',
];

import AccountingTicketTemplates from '@/components/TicketsAccountingPeriod/AccountingTicketTemplates.vue';
import type { AccountingTicketTemplateType } from '@/generated/graphql.ts';

const accountingTicketTemplates = ref<AccountingTicketTemplateType[]>([]);
const selectedTemplate = ref<AccountingTicketTemplateType | null>(null);

// Field-specific pending placeholders
const subjectPendingFields = computed<string[]>(() => {
  if (!selectedTemplate.value) return [];
  const names = extractPlaceholderNames(selectedTemplate.value.subject || '');
  return props.periodName ? names.filter(n => n !== PERIOD_PLACEHOLDER_NAME) : names;
});

const messagePendingFields = computed<string[]>(() => {
  if (!selectedTemplate.value) return [];
  const names = extractPlaceholderNames(selectedTemplate.value.message || '');
  return props.periodName ? names.filter(n => n !== PERIOD_PLACEHOLDER_NAME) : names;
});

// Hints per field
const subjectHint = computed<string>(() => {
  return `The subject cannot exceed ${MAX_LENGTH_SUBJECT} characters.`;
});

const onTemplateSelect = (tpl: AccountingTicketTemplateType) => {
  if (!tpl) return;
  selectedTemplate.value = tpl;
  const prepare = (t?: string | null) => prettifyPlaceholders(replacePeriodBeforePrettify(t, props.periodName));
  subjectTicket.value = prepare(tpl.subject);
  message.value = prepare(tpl.message);
  nextTick(() => {
    subjectInputRef.value?.resetValidation?.();
    messageInputRef.value?.resetValidation?.();
    formCreateTicketSubjectRef.value?.resetValidation?.();
  });
};

const existingFiles = ref<Array<FileDto>>([]);
const isUploadingFiles = ref<boolean>(false);

const fileUploaderComponent = ref<FileUploaderComponentType | null>(null);
const uploadedFileIds = ref<string[]>([]);

const isSubmitting = ref<boolean>(false);

const hasVisiblePlaceholders = computed<boolean>(() => {
  return subjectTicket.value.includes(PLACEHOLDER_VISIBLE) || message.value.includes(PLACEHOLDER_VISIBLE);
});

const isMessageInputDisabled = computed<boolean>(() => {
  return isSubmitting.value || isUploadingFiles.value;
});

// Select a visible placeholder token (___) on click so typing replaces it
const subjectInputRef = ref<InstanceType<typeof QInput> | null>(null);
const messageInputRef = ref<InstanceType<typeof QInput> | null>(null);

const selectPlaceholderAtCursor = (
  el: HTMLInputElement | HTMLTextAreaElement | null,
  value: string,
) => {
  if (!el) return;
  const start = el.selectionStart ?? -1;
  if (start < 0) return;
  const range = getPlaceholderRangeAtPos(value, start);
  if (!range) return;
  // If not already selected, select the token
  if (el.selectionStart !== range[0] || el.selectionEnd !== range[1]) {
    el.focus();
    el.setSelectionRange(range[0], range[1]);
  }
};

const handleClickSubject = (): void => {
  const el = subjectInputRef.value?.getNativeElement?.() as HTMLInputElement | null;
  if (!el) return;
  selectPlaceholderAtCursor(el, subjectTicket.value);
};

const handleClickMessage = (): void => {
  const el = messageInputRef.value?.getNativeElement?.() as HTMLTextAreaElement | null;
  if (!el) return;
  selectPlaceholderAtCursor(el, message.value);
};

const { runCreateReviewGate } = useMessageValidation();

const submitCreateTicketSubject = async (): Promise<void> => {
  const isValid = await formCreateTicketSubjectRef.value?.validate();
  if (!isValid) return;
  if (hasVisiblePlaceholders.value) {
    Notify.create({
      message: `Please replace all placeholders (${PLACEHOLDER_VISIBLE}) in subject and message before creating`,
      color: 'negative',
      position: 'top',
    });
    return;
  }

  // Advisory validation gate: review subject + message together before creating (never blocks).
  let messageToSend = message.value;
  if (subjectTicket.value?.trim() || message.value?.trim()) {
    isSubmitting.value = true;
    const gate = await runCreateReviewGate(subjectTicket.value, message.value).finally(() => {
      isSubmitting.value = false;
    });
    if (gate.action === MessageGateAction.Cancel) return;
    subjectTicket.value = gate.subject;
    messageToSend = gate.message;
  }

  const currentTransactionId = localTransactionContext.value?.transactionId;
  const transactionIds = currentTransactionId ? [currentTransactionId] : [];

  const payload: CreateTaskInputDto = {
    companyId: props.companyId,
    periodIds: [props.periodFolderId],
    subject: subjectTicket.value,
    fileIds: localFilesContext.value.map(f => f.id),
    transactionIds,
  };
  try {
    isSubmitting.value = true;
    const ticketId = await createTicket(payload);
    if (!ticketId) {
      Notify.create({
        message: 'Failed to create question',
        color: 'negative',
        position: 'top',
      });
      return;
    }
    Notify.create({
      message: 'Successfully created question',
      color: 'positive',
      position: 'top',
    });
    let fileIds;
    if (uploadedFileIds.value?.length > 0) {
      fileIds = await uploadSelectedFiles();
    }
    await sendTicketMessage(ticketId, props.periodFolderId, messageToSend, fileIds);
    clearMessageInput();
    onDialogOK();
  } finally {
    isSubmitting.value = false;
  }
};

/**
 * Uploads any pending files and returns their IDs
 * This function is used when sending a message to upload and attach files
 *
 * @returns Array of file IDs to attach to the message
 */
const uploadSelectedFiles = async () => {
  if (fileUploaderComponent.value && typeof fileUploaderComponent.value.uploadPendingFiles === 'function') {
    return await fileUploaderComponent.value.uploadPendingFiles();
  }
  return uploadedFileIds.value;
};

const handleUnlinkLocalFile = (remainingIds: string[]) => {
  localFilesContext.value = localFilesContext.value.filter(f => remainingIds.includes(f.id));
};

const handleUnlinkLocalTransaction = () => {
  localTransactionContext.value = null;
};

const clearMessageInput = () => {
  message.value = '';
  uploadedFileIds.value = [];
  if (fileUploaderComponent.value) {
    fileUploaderComponent.value.clearFiles();
  }
};
</script>

<template>
  <q-dialog
    ref="createSubjectTicketDialogRef"
    persistent
  >
    <q-card class="dialog-card column no-wrap">
      <q-card-section class="row justify-between">
        <div class="text-h6">
          Create a question
        </div>
        <q-btn
          v-close-popup
          icon="close"
          color="primary"
          flat
          round
          dense
        />
      </q-card-section>
      <q-separator />

      <q-form
        ref="formCreateTicketSubjectRef"
        class="column no-wrap form-flex-fill"
        @submit.prevent="submitCreateTicketSubject"
      >
        <q-card-section
          class="col scroll"
        >
          <AccountingTicketTemplates
            @loaded="(t) => (accountingTicketTemplates = t)"
            @select="onTemplateSelect"
          />

          <div class="column q-gutter-y-md bg-grey-1 q-ma-md q-pa-md rounded-borders">
            <TicketContextFiles
              :files-context="localFilesContext"
              :can-unlink="canManageTickets"
              hide-navigation
              @unlink-document="handleUnlinkLocalFile"
            />
            <TicketContextTransaction
              :transaction-context="localTransactionContext"
              :company-id="props.companyId"
              :folder-id="props.folderId"
              :period-folder-id="props.periodFolderId"
              :can-unlink="canManageTickets"
              hide-navigation
              @unlink-transaction="handleUnlinkLocalTransaction"
            />
            <div>
              <div class="text-body1 q-mb-md text-grey-9 text-weight-medium">
                Enter the subject of the question
              </div>
              <q-input
                ref="subjectInputRef"
                v-model.trim="subjectTicket"
                autofocus
                outlined
                :disable="isSubmitting"
                name="subjectTicket"
                :rules="subjectTicketRules"
                lazy-rules="ondemand"
                @click="handleClickSubject"
              >
                <template #hint>
                  <div class="row items-center q-gutter-sm">
                    <span>{{ subjectHint }}</span>
                    <template v-if="subjectPendingFields.length">
                      <span class="text-weight-bold text-primary">Please fill:</span>
                      <div class="row items-center q-gutter-xs">
                        <q-chip
                          v-for="name in subjectPendingFields"
                          :key="name"
                          color="primary"
                          text-color="white"
                          dense
                          size="sm"
                          class="q-px-sm"
                        >
                          {{ name }}
                        </q-chip>
                      </div>
                    </template>
                  </div>
                </template>
              </q-input>
            </div>
            <div class="q-my-sm" />
            <div>
              <div class="text-body1 q-mb-md text-grey-9 text-weight-medium">
                Add message
              </div>
              <q-input
                ref="messageInputRef"
                v-model.trim="message"
                type="textarea"
                outlined
                stack-label
                placeholder="Type your message here..."
                rows="4"
                class="q-mb-md"
                autogrow
                :disable="isMessageInputDisabled"
                :rules="messageTicketRules"
                lazy-rules="ondemand"
                @click="handleClickMessage"
              >
                <template #hint>
                  <div
                    v-if="messagePendingFields.length"
                  >
                    <div class="row items-center q-gutter-sm">
                      <span class="text-weight-bold text-primary">Please fill:</span>
                      <div class="row items-center q-gutter-xs">
                        <q-chip
                          v-for="name in messagePendingFields"
                          :key="name"
                          color="primary"
                          text-color="white"
                          dense
                          size="sm"
                          class="q-px-sm"
                        >
                          {{ name }}
                        </q-chip>
                      </div>
                    </div>
                  </div>
                </template>
              </q-input>

              <div class="row items-center q-mb-md q-pt-md">
                <FileUploader
                  v-if="props.companyId"
                  ref="fileUploaderComponent"
                  :company-id="props.companyId"
                  :disabled="isSubmitting"
                  :existing-files="existingFiles"
                  @update:file-ids="uploadedFileIds = $event"
                  @uploading="isUploadingFiles = $event"
                />
              </div>
            </div>
          </div>
        </q-card-section>
        <q-separator />
        <q-card-actions
          align="right"
          class="text-primary q-my-md q-mx-sm"
        >
          <q-btn
            v-close-popup
            flat
            no-caps
            label="Cancel"
          />
          <q-btn
            flat
            type="submit"
            color="dark"
            no-caps
            :loading="isSubmitting"
            :disable="isSubmitting"
            label="Create"
          />
        </q-card-actions>
      </q-form>
    </q-card>
  </q-dialog>
</template>

<style scoped lang="scss">
.dialog-card {
  width: 90vw;
  max-width: 840px;
  max-height: 90vh;
}

.form-flex-fill {
  flex: 1 1 auto;
  min-height: 0;
}
</style>
